import { useLocation, useParams } from 'react-router-dom';
import CommentContainer from '~/components/Comment/CommentContainer';
import { DefaultButton, FlexStack } from '~/components/atomic';
import { HelmetHead } from '~/components/common';
import CharacterImage from '~/components/common/CharacterCard/CharacterImage/CharacterImage';
import CharacterLikedCount from '~/components/common/CharacterCard/CharacterLikedCount/CharacterLikedCount';
import CreatorTag from '~/components/common/CharacterCard/CreatorTag/CreatorTag';
import { useGetPersonaDetailsQuery } from '~/features/character';
import { useCharacterLikeMutation } from '~/features/user';
import { useMoveChatRoom } from '~/hooks';
import { parseTextWithItalics } from '~/utils';
import BlockModal from '../../common/BottomSheet/BlockNReport/BlockModal';
import CharacterLike from '../CharacterLike/CharacterLike';
import * as S from './CharacterDetail.styled';
import CharacterDetailError from './CharacterDetailError';

interface CharacterDetailProps {
  showBlockCharacter: boolean;
  setShowBlockCharacter: (state: boolean) => void;
}

export default function CharacterDetail({
  showBlockCharacter,
  setShowBlockCharacter,
}: CharacterDetailProps) {
  const { id } = useParams<{ id: string }>();
  const { state } = useLocation();

  const character_id = id || '';
  const fromChatRoom = state?.fromChatRoom ?? false;

  const { data } = useGetPersonaDetailsQuery({
    character_id,
    // type: state.type,
  });
  const { handleCreateRoomClick } = useMoveChatRoom({
    chatRoomLoadingBg: !data ? '' : data?.character_image,
  });

  const { mutate: handleCharacterLike } = useCharacterLikeMutation();

  const handleLikeClick = (id: string, isLiked: boolean) => {
    handleCharacterLike({
      character_id: id,
      operation: isLiked ? 'unlike' : 'like',
    });
  };

  if (!data) {
    return <CharacterDetailError />;
  }

  const renderTextSegments = (text: string) => {
    const segments = parseTextWithItalics(text);
    return segments.map(({ italic, text }, index) =>
      text === '\n' ? (
        <br key={index} />
      ) : (
        <p className={italic ? 'italic' : ''} key={index}>
          {text}
        </p>
      ),
    );
  };

  return (
    <>
      <HelmetHead title={`${data.character_name} - details`} />
      {/* {isCreateRoomPending && <FullScreenLoading />} */}

      <S.Container>
        <FlexStack spacing={32} style={{ position: 'relative' }}>
          <FlexStack spacing={16} style={{ position: 'relative' }}>
            <CharacterImage
              id={character_id}
              image={data.character_image}
              is_liked={data.is_liked}
              handleLikeClick={() =>
                handleLikeClick(character_id, data.is_liked)
              }
            />
            <FlexStack spacing={10}>
              <S.Name>{data.character_name}</S.Name>
              <S.Desc>
                <CharacterLikedCount liked_count={data.liked_count} />
                <CreatorTag create_username={data.creator_name} />
              </S.Desc>
            </FlexStack>

            <CharacterLike
              character_id={character_id}
              emojis={data.emojis}
              type={state.type}
            />
          </FlexStack>
          <CommentContainer viewComment={data.comment} />

          <FlexStack spacing={32}>
            <S.Section>
              <h2>Opening</h2>
              <div>{renderTextSegments(data.opening)}</div>
            </S.Section>
            <S.Divider />
            <S.Section>
              <h2>Tagline</h2>
              <div>
                <p>{data.tag_line}</p>
              </div>
            </S.Section>
            <S.Divider />
            <S.Section>
              <h2>Character detail (Persona)</h2>
              <div>
                {data.show_persona ? (
                  renderTextSegments(data.persona)
                ) : (
                  <p className='private'>
                    Details about this character are private
                  </p>
                )}
              </div>
            </S.Section>
          </FlexStack>
        </FlexStack>

        {!fromChatRoom && (
          <S.ButtonBox>
            <DefaultButton
              onClick={() => handleCreateRoomClick(character_id, state.type)}
            >
              Start a chat
            </DefaultButton>
          </S.ButtonBox>
        )}
      </S.Container>

      {typeof data !== 'string' && (
        <BlockModal
          character_id={character_id}
          showBlockCharacter={showBlockCharacter}
          setShowBlockCharacter={setShowBlockCharacter}
        />
      )}
    </>
  );
}
